<!-- =========================================================================================
  File Name: AddNewDataSidebar.vue
  Description: Add New Data - Sidebar component
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->
<i18n src="@/locales/modules/DeviceTranslate.json"></i18n>

<template>
	<el-dialog
		append-to-body
		destroy-on-close
		:before-close="
			setDialogHide => {
				handleCloseDialog(setDialogHide, isFormDirty);
			}
		"
		class="custom-dialog"
		:title="$t(Object.entries(this.data).length === 0 ? 'AddNewDevice' : 'UpdateDevice')"
		:visible.sync="isSidebarActiveLocal"
	>
		<PerfectScrollbar ref="vueScrollBar" class="scroll-area--data-list" :settings="settings">
			<el-collapse v-model="activeGeneral" class="cbs-collapse mt-0 device-form--create-new">
				<el-collapse-item name="GeneralInfo">
					<template slot="title">
						<div>
							<h6 class="uppercase font-bold ml-6">{{ $t("GeneralInfo") }}</h6>
						</div>
					</template>
					<div class="p-6 pb-0" style="z-index: 99999">
						<!-- NAME -->
						<div>
							<label for class="vs-input--label">
								{{ $t("Name") }}
								<span class="text-danger">*</span>:
							</label>
							<cbs-input v-model="dataName" class="w-full" name="name" :placeholder="`${$t('Actions.Input')} ${$t('Name')}`" :data-vv-as="$t('Name')" v-validate="'required'" />
							<span class="text-danger text-sm break-normal" v-show="errors.has('name')">{{ errors.first("name") }}</span>
						</div>

						<!-- CODE -->
						<cbs-input :label="`${$t('Code')}:`" :placeholder="`${$t('Actions.Input')} ${$t('Code')}`" v-model="dataCode" class="mt-5 w-full" name="code" />

						<!-- CATEGORY -->
						<div class="mt-5">
							<label for class="vs-input--label">
								{{ $t("Category") }}
								<span class="text-danger">*</span>:
							</label>

							<v-select
								taggable
								class="select-tree"
								ref="selectCategoryRef"
								v-model="categoryId"
								label="text"
								name="category"
								v-validate="'required'"
								:options="categoryChoices"
								:reduce="category => category.value"
								:data-vv-as="$t('Category')"
								:create-option="newOptionText => handleAddingNewOption(newOptionText, categoryChoices)"
								:placeholder="$t('InputCategoryNameAndEnterToCreateNewOne')"
								@option:created="handleCreateNewCategory"
							>
								<template v-slot:option="option">
									<div :class="`flex items-center pl-${option.depth > 1 ? option.depth * 2 : option.depth}`">
										<i class="mr-2 nucleo nucleo-shape-oval-3"></i>
										{{ option.text }}
										<vs-chip v-if="!option.value" class="ml-2" color="success">{{ $t("Actions.AddNew") }}</vs-chip>
									</div>
								</template>
								<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
							</v-select>

							<span class="text-danger text-sm break-normal" v-show="errors.has('category')">{{ errors.first("category") }}</span>
						</div>
						<!-- MANAGER -->
						<div class="mt-5">
							<label for class="vs-input--label">{{ $t("Manager") }}:</label>
							<v-select
								class="select-multiple"
								:placeholder="`${$t('Actions.Choose')} ${$t('Manager').toLowerCase()}`"
								v-model="managerId"
								:reduce="manager => manager.value"
								label="text"
								name="manager"
								multiple
								:options="managerChoices"
							>
								<span slot="no-options">
									{{ $t("Notify.NoMatchingOptions") }}
								</span>
							</v-select>
							<!-- <cbs-relations-humanroles
								:data-vv-as="$t('Manager')"
								:placeholder="`${$t('Actions.Choose')} ${$t('Manager').toLowerCase()}`"
								name="manager"
								v-model="managerId"
								:type="1"
								:for="'people'"
								:modelId="data.parent && data.parent.id"
								:modelType="$constant.RELATION_MODELS.TODO"
							/> -->
						</div>
						<!-- System Tags -->
						<CbsTags v-model="tags" />
					</div>
				</el-collapse-item>
			</el-collapse>

			<el-collapse class="cbs-collapse mt-0">
				<el-collapse-item name="ExtraInfo">
					<template slot="title">
						<div>
							<h6 class="uppercase font-bold ml-6">{{ $t("ExtraInfo") }}</h6>
						</div>
					</template>
					<div class="p-6 pb-0">
						<!-- SERIAL -->
						<cbs-input :label="`${$t('Serial')}:`" :placeholder="`${$t('Actions.Input')} ${$t('Serial')}`" v-model="dataSerial" class="w-full" name="serial" />
						<!-- MAINTAIN HRS -->
						<div class="manufacture-year mt-5">
							<label for class="vs-input--label">{{ $t("MaintainHour") }}:</label>
							<cbs-input
								type="number"
								v-model="maintainHrs"
								@keypress="filterKey"
								class="w-full"
								min="0"
								name="maintain-hour"
								:placeholder="`${$t('Actions.Input')} ${$t('MaintainHour')}`"
								v-validate="{ numeric: true, min_value: 0 }"
								:data-vv-as="$t('MaintainHour')"
							/>
							<span class="text-danger text-sm break-normal" v-show="errors.has('maintain-hour')">{{ errors.first("maintain-hour") }}</span>
						</div>
						<!-- INVENTORY -->
						<div class="mt-5">
							<label for class="vs-input--label">{{ $t("Inventory") }}:</label>
							<v-select
								taggable
								ref="selectInventoryRef"
								v-model="inventoryId"
								label="text"
								name="inventory"
								:options="inventoryChoices"
								:reduce="inventory => inventory.value"
								:create-option="newOptionText => handleAddingNewOption(newOptionText, inventoryChoices)"
								:placeholder="$t('InputInventoryNameAndEnterToCreateNewOne')"
								@option:created="handleCreateNewInventory"
							>
								<template v-slot:option="option">
									<div class="flex items-center">
										<span>{{ option.text }}</span>
										<vs-chip v-if="!option.value" class="ml-2" color="success">{{ $t("Actions.AddNew") }}</vs-chip>
									</div>
								</template>
								<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
							</v-select>
						</div>

						<!-- PRICE -->
						<div class="mt-5">
							<label for class="vs-input--label">{{ $t("Price") }}:</label>
							<vx-input-group prependClasses="border border-solid border-grey border-r-0">
								<template slot="prepend">
									<div class="prepend-text text-black">
										<span>VNĐ</span>
									</div>
								</template>
								<cbs-masked-input
									dataType="int"
									class="vs-inputx vs-input--input normal w-full"
									v-model.number="dataPrice"
									name="price"
									:userSetting="userSettings"
									v-validate="{ min_value: 0 }"
									:data-vv-as="$t('Price')"
								/>
							</vx-input-group>
							<span class="text-danger text-sm break-normal" v-show="errors.has('price')">{{ errors.first("price") }}</span>
						</div>

						<!-- MANUFACTURE YEAR -->
						<div class="manufacture-year mt-5">
							<label for class="vs-input--label">{{ $t("ManufactureYear") }}:</label>
							<cbs-input
								type="number"
								v-model="manufactureYear"
								@keypress="filterKey"
								class="w-full"
								min="0"
								name="manufacture-year"
								:placeholder="`${$t('Actions.Input')} ${$t('ManufactureYear')}`"
								v-validate="{ numeric: true, min_value: 0 }"
								:data-vv-as="$t('ManufactureYear')"
							/>
							<span class="text-danger text-sm break-normal" v-show="errors.has('manufacture-year')">{{ errors.first("manufacture-year") }}</span>
						</div>

						<!-- WARRANTY -->
						<cbs-input :label="`${$t('Warranty')}:`" :placeholder="$t('InputWarranty')" v-model="warranty" class="mt-5 w-full" name="warranty" />

						<!-- OPERATION DATE -->
						<div class="operation-time mt-5">
							<label for class="vs-input--label">{{ $t("OperationDate") }}:</label>
							<vx-input-group>
								<template slot="append">
									<div class="append-text text-black">
										<i class="nucleo nucleo-calendar-date" @click="triggerClickDatetimePicker"></i>
									</div>
								</template>

								<cbs-date-picker
									clearable
									class="w-full"
									prefix-icon="''"
									ref="dateTimePickerRef"
									:format="userDateFormat"
									value-format="yyyy-MM-dd"
									:placeholder="`${$t('Actions.Input')} ${$t('OperationDate').toLowerCase()}`"
									v-model="operationDate"
								/>
							</vx-input-group>
						</div>
						<!-- inspection_deadline -->
						<div class="operation-time mt-5">
							<label for class="vs-input--label">{{ $t("InspectionDeadline") }}:</label>
							<vx-input-group>
								<template slot="append">
									<div class="append-text text-black">
										<i class="nucleo nucleo-calendar-date" @click="triggerClickDatetimePicker"></i>
									</div>
								</template>

								<cbs-date-picker
									clearable
									class="w-full"
									prefix-icon="''"
									ref="dateTimePickerRef"
									:format="userDateFormat"
									value-format="yyyy-MM-dd"
									:placeholder="`${$t('Actions.Input')} ${$t('InspectionDeadline').toLowerCase()}`"
									v-model="inspectionDeadline"
								/>
							</vx-input-group>
						</div>

						<!-- MAINTAINANCE TIMES -->
						<cbs-input
							:label="`${$t('MaintenanceTimes')}:`"
							v-model="maintenanceTimes"
							class="mt-5 w-full"
							type="number"
							min="0"
							@keypress="filterKey"
							v-validate="{
								min_value: 0
							}"
							:data-vv-as="$t('MaintenanceTimes')"
							name="maintenance-times"
						/>
						<span class="text-danger text-sm break-normal" v-show="errors.has('maintenance-times')">{{ errors.first("maintenance-times") }}</span>

						<!-- TOTAL MAINTAINANCE COST -->
						<div class="my-5">
							<label for class="vs-input--label">{{ $t("TotalMaintenanceCost") }}:</label>
							<vx-input-group prependClasses="border border-solid border-grey border-r-0">
								<template slot="prepend">
									<div class="prepend-text text-black">
										<span>VNĐ</span>
									</div>
								</template>
								<cbs-masked-input
									dataType="int"
									class="vs-inputx vs-input--input normal w-full"
									v-model.number="totalMaintenanceCost"
									name="total-maintenance-cost"
									:userSetting="userSettings"
									v-validate="{ min_value: 0 }"
									:data-vv-as="$t('TotalMaintenanceCost')"
								/>
							</vx-input-group>
							<span class="text-danger text-sm break-normal" v-show="errors.has('total-maintenance-cost')">{{ errors.first("total-maintenance-cost") }}</span>
						</div>

						<!-- Description -->
						<div class="mt-5 mb-3">
							<label for class="vs-input--label">{{ $t("Description") }}:</label>
							<vs-textarea v-model="description" :placeholder="`${$t('Actions.Input')} ${$t('Description').toLowerCase()}`" />
						</div>

						<!-- Attach images -->
						<div class="mt-5 mb-3">
							<label for class="vs-input--label">{{ $t("UploadImage") }}:</label>
							<cbs-upload-ref v-model="images" accept="image/*" />
							<p class="italic font-weight-normal text-gray-3">{{ $t("CanAttachPicturesOrDocumentsRelatedToTheDevice", { target: $t("Device") }) }}</p>
						</div>

						<!-- DEVICE STATUS -->
						<div v-show="dataId && mappedTransitsData.length" class="mb-5">
							<label for class="vs-input--label">{{ $t("ChangeStatus") }}:</label>
							<input v-model="status" name="status" type="hidden" />
							<span class="text-danger text-sm break-normal block" v-show="errors.has('status')">{{ errors.first("status") }}</span>
							<vs-row class="mt-2">
								<vs-col :key="item.id" :value="item.id" v-for="item in mappedTransitsData">
									<div class="device-status">
										<vs-radio :key="status" v-model="status" :vs-value="item.transitState.id">
											<div class="text-black">
												{{ $t("ChangeFrom") }}
												<span class="font-bold" :style="{ color: item.currentDeviceState.color }"> {{ item.currentDeviceState.name }}</span>
												{{ $t("To").toLowerCase() }}
												<span class="font-bold" :style="{ color: item.transitState.color }"> {{ item.transitState.name }}</span>
											</div>
										</vs-radio>
									</div>
								</vs-col>
							</vs-row>
						</div>
					</div>
				</el-collapse-item>
			</el-collapse>
		</PerfectScrollbar>

		<div class="flex justify-center flex-wrap items-center" slot="footer">
			<vs-button :type="dataId ? 'filled' : 'border'" class="w-full sm:w-1/3 mb-3 sm:mb-0 sm:mr-6 font-bold" @click.stop="submitData(!!dataId)">
				{{ dataId ? $t("Actions.Save") : $t("Actions.SaveAndRecreate") }}
			</vs-button>
			<vs-button v-show="!dataId" class="w-full sm:w-1/3 font-bold" @click.stop="submitData(true)">{{ $t("Actions.Save") }}</vs-button>
		</div>
	</el-dialog>
</template>

<script>
import _ from "lodash";
import vSelect from "vue-select";
import { mapGetters } from "vuex";
import employeeGql from "@/http/requests/inside/employee_gql";
import CbsUploadRef from "@/components/cbs-upload-ref/CbsUploadRef";
import CbsTags from "@/components/cbs-tags/CbsTags";

export default {
	props: {
		isSidebarActive: {
			type: Boolean,
			required: true
		},
		data: {
			type: Object,
			default: () => {}
		}
	},
	watch: {
		isSidebarActive(val) {
			if (!val) return;
			if (Object.entries(this.data).length === 0) {
				this.initValues();
			} else {
				let {
					inventory_id,
					manager_id,
					category,
					id,
					images,
					name,
					code,
					serial,
					price,
					used_at,
					checked_times,
					repaired_total_cost,
					manufacture_year,
					warranty,
					status,
					mappedTransitsData,
					is_configured,
					warning_threshold,
					alert_threshold,
					current,
					description,
					maintain_hrs,
					tags,
					inspection_deadline
				} = this.data;

				this.dataId = id || null;
				this.inventoryId = inventory_id || null;
				this.managerId = manager_id || null;
				this.categoryId = _.get(category, "id") || null;
				this.images = images || null;
				this.dataCode = code || "";
				this.dataSerial = serial || "";
				this.dataName = name || "";
				this.dataPrice = price || 0;
				this.operationDate = used_at || null;
				this.maintenanceTimes = checked_times || 0;
				this.totalMaintenanceCost = repaired_total_cost || 0;
				this.manufactureYear = manufacture_year || "";
				this.warranty = warranty || "";
				this.status = status;
				this.mappedTransitsData = mappedTransitsData || [];
				this.accessoryCurrentValue = current || 0;
				this.isConfigAccessory = is_configured || false;
				this.accessoryAlertLimit = alert_threshold || null;
				this.accessoryWarningLimit = warning_threshold || null;
				this.description = description || "";
				this.maintainHrs = maintain_hrs;
				this.tags = tags || [];
				this.inspectionDeadline = inspection_deadline;
			}

			this.isFormDirty = false;
			this.initFormData = {
				dataId: this.dataId,
				dataName: this.dataName,
				dataCode: this.dataCode,
				dataSerial: this.dataSerial,
				status: this.status,
				inventoryId: this.inventoryId,
				managerId: this.managerId,
				categoryId: this.categoryId,
				manufactureYear: this.manufactureYear,
				warranty: this.warranty,
				operationDate: this.operationDate,
				maintenanceTimes: this.maintenanceTimes,
				totalMaintenanceCost: this.totalMaintenanceCost,
				images: this.images,
				dataPrice: this.dataPrice,
				isConfigAccessory: this.isConfigAccessory,
				accessoryCurrentValue: this.accessoryCurrentValue,
				accessoryAlertLimit: this.alert_threshold,
				accessoryWarningLimit: this.warning_threshold,
				isResetAccessoryCurrentValue: this.isResetAccessoryCurrentValue,
				description: this.description,
				maintainHrs: this.maintainHrs,
				tags: this.tags,
				inspectionDeadline: this.inspectionDeadline
			};
		},

		computedFormData: {
			handler: function(newVal) {
				this.isFormDirty = JSON.stringify(newVal) !== JSON.stringify(this.initFormData) && newVal.dataId === this.initFormData.dataId;
			},
			deep: true
		}
	},
	data() {
		return {
			dataId: null,
			dataName: "",
			dataCode: "",
			dataSerial: "",
			status: null,
			inventoryId: null,
			managerId: null,
			categoryId: null,
			manufactureYear: "",
			warranty: "",
			operationDate: null,
			maintenanceTimes: 0,
			totalMaintenanceCost: 0,
			images: null,
			dataPrice: 0,

			listManager: [],
			mappedTransitsData: [],

			isConfigAccessory: false,
			accessoryCurrentValue: 0,
			accessoryWarningLimit: null,
			accessoryAlertLimit: null,
			isResetAccessoryCurrentValue: false,

			settings: {
				// perfectscrollbar settings
				maxScrollbarLength: 60,
				wheelSpeed: 0.6
			},
			initFormData: {},
			isFormDirty: false,
			activeGeneral: ["GeneralInfo"],
			description: "",
			maintainHrs: "",
			tags: [],
			inspectionDeadline: null
		};
	},
	computed: {
		isSidebarActiveLocal: {
			get() {
				return this.isSidebarActive;
			},
			set(val) {
				if (!val) {
					this.$emit("closeSidebar");
				}
			}
		},
		...mapGetters({
			getFormattedFlattenTreeCategories: "deviceManagement/getFormattedFlattenTreeCategories"
		}),
		categoryChoices() {
			return (this.getFormattedFlattenTreeCategories || []).map(category => ({
				value: category.id,
				text: category.name,
				depth: category.depth
			}));
		},
		inventoryChoices() {
			return this.$store.state.deviceManagement.inventories.map(inventory => ({
				value: inventory.id,
				text: inventory.name
			}));
		},
		managerChoices() {
			let listManager = [];
			if (this.listManager) {
				listManager = this.listManager.map(manager => ({
					value: manager.id,
					text: manager.display_name
				}));
			}
			return listManager;
		},
		computedFormData() {
			return {
				dataId: this.dataId,
				dataName: this.dataName,
				dataCode: this.dataCode,
				dataSerial: this.dataSerial,
				status: this.status,
				inventoryId: this.inventoryId,
				managerId: this.managerId,
				categoryId: this.categoryId,
				manufactureYear: this.manufactureYear,
				warranty: this.warranty,
				operationDate: this.operationDate,
				maintenanceTimes: this.maintenanceTimes,
				totalMaintenanceCost: this.totalMaintenanceCost,
				images: this.images,
				dataPrice: this.dataPrice,
				isConfigAccessory: this.isConfigAccessory,
				accessoryCurrentValue: this.accessoryCurrentValue,
				accessoryAlertLimit: this.alert_threshold,
				accessoryWarningLimit: this.warning_threshold,
				isResetAccessoryCurrentValue: this.isResetAccessoryCurrentValue,
				description: this.description,
				maintainHrs: this.maintainHrs,
				tags: this.tags,
				inspectionDeadline: this.inspectionDeadline
			};
		},
		accessoryWarningLimitRules() {
			const rules = {
				required: !!this.isConfigAccessory,
				decimal: true
			};
			if (!_.isNull(this.accessoryAlertLimit)) {
				rules["isSmaller"] = {
					name: this.$t("AccessoryLimitAlertLevel"),
					value: Number(this.accessoryAlertLimit)
				};
			}
			return rules;
		}
	},
	methods: {
		initValues() {
			if (this.data.id) return;
			this.dataId = null;
			this.dataName = "";
			this.dataCode = "";
			this.dataSerial = "";
			this.status = null;
			this.categoryId = null;
			this.inventoryId = null;
			this.managerId = null;
			this.manufactureYear = "";
			this.warranty = "";
			this.operationDate = null;
			this.maintenanceTimes = 0;
			this.totalMaintenanceCost = 0;
			this.dataPrice = 0;
			this.images = null;
			this.isConfigAccessory = false;
			this.accessoryCurrentValue = 0;
			this.accessoryAlertLimit = null;
			this.accessoryWarningLimit = null;
			this.description = "";
			this.maintainHrs = "";
			this.tags = [];
			this.inspectionDeadline = null;
			this.$validator.reset();
		},
		handleCreateOrUpdateSuccess(isCloseForm) {
			if (isCloseForm) {
				this.$emit("closeSidebar");
			}
			this.initValues();
			this.isFormDirty = false;
			this.$helper.notifySuccess({
				context: this,
				options: {
					text: this.$t(`${this.dataId ? "UpdateDeviceSuccessful" : "CreateDeviceSuccessful"}`)
				}
			});
			this.$emit("refetchDataList");
		},
		async handleCreateOrUpdateDevice(dataObj, isCloseForm) {
			try {
				this.$vs.loading();
				if (this.dataId) {
					await this.$store.dispatch("deviceManagement/updateDevice", {
						...dataObj,
						id: this.dataId
					});
				} else {
					await this.$store.dispatch("deviceManagement/addDevice", dataObj);
				}

				this.handleCreateOrUpdateSuccess(isCloseForm);
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		submitData(isCloseForm = false) {
			this.$validator.validateAll().then(result => {
				if (result) {
					const images = this.images ? this.images.map(file => Number(file.id)) : [];
					let tag_ids = this.tags;
					if (this.tags && this.tags[0] && this.tags[0].id) {
						tag_ids = _.map(this.tags, "id"); // [12, 14, 16, 18]
					}
					const dataObj = {
						name: this.dataName,
						code: this.dataCode,
						serial: this.dataSerial,
						used_at: this.operationDate || null,
						category_id: this.categoryId,
						inventory_id: this.inventoryId,
						manage_employees: this.managerId ? this.managerId : [],
						price: this.dataPrice || 0,
						mfg: this.manufactureYear,
						wd: this.warranty,
						checked_times: this.maintenanceTimes,
						repaired_total_cost: this.totalMaintenanceCost || 0,
						warning_threshold: this.accessoryWarningLimit,
						alert_threshold: this.accessoryAlertLimit,
						images: images,
						description: this.description,
						maintain_hrs: this.maintainHrs && this.maintainHrs !== "" ? this.maintainHrs : 0,
						tag_ids: tag_ids,
						inspection_deadline: this.inspectionDeadline
					};
					if (this.status) {
						dataObj.status = this.status;
					}
					if (this.dataId && this.isResetAccessoryCurrentValue) {
						dataObj.is_reset_counter = this.isResetAccessoryCurrentValue;
					}
					if (!this.isConfigAccessory) {
						dataObj.warning_threshold = null;
						dataObj.alert_threshold = null;
					}

					// if(typeof this.tags ==)

					this.handleCreateOrUpdateDevice(dataObj, isCloseForm);
				}
			});
		},

		async fetchFormOptions() {
			try {
				const [employeesResponse] = await Promise.all([
					employeeGql.fetchEmployees({ limit: -1 }),
					this.$store.dispatch("deviceManagement/fetchInventories", {
						limit: -1,
						fetchPolicy: "no-cache"
					}),
					this.$store.dispatch("deviceManagement/fetchCategories", {
						limit: -1,
						fetchPolicy: "no-cache"
					})
				]);

				this.listManager = employeesResponse.employees || [];
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			}
		},
		filterKey($event) {
			const keyCode = $event.keyCode ? $event.keyCode : $event.which;
			if (keyCode === 45 || keyCode === 189 || keyCode === 46 || keyCode === 101) {
				$event.preventDefault();
			}
		},
		handleAddingNewOption(newOptionText, listOptions) {
			const existedOption = listOptions.find(option => option.text === newOptionText);
			if (!existedOption) {
				return { text: newOptionText };
			}
			return existedOption;
		},
		async handleCreateNewCategory(newOption) {
			const refLoading = this.$refs && this.$refs.selectCategoryRef && this.$refs.selectCategoryRef.toggleLoading;
			try {
				if (refLoading) {
					refLoading(true);
				}
				const response = await this.$store.dispatch("deviceManagement/addCategory", { name: newOption.text });
				this.categoryId = response.id;
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t("CreateCategorySuccessful")
					}
				});
			} catch (error) {
				this.categoryId = null;
				this.$helper.notifyError({ context: this, error });
			} finally {
				if (refLoading) {
					refLoading(false);
				}
			}
		},
		async handleCreateNewInventory(newOption) {
			const refLoading = this.$refs && this.$refs.selectInventoryRef && this.$refs.selectInventoryRef.toggleLoading;
			try {
				if (refLoading) {
					refLoading(true);
				}
				const response = await this.$store.dispatch("deviceManagement/addInventory", { name: newOption.text });
				this.inventoryId = response.id;
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t("CreateInventorySuccessful")
					}
				});
			} catch (error) {
				this.inventoryId = null;
				this.$helper.notifyError({ context: this, error });
			} finally {
				if (refLoading) {
					refLoading(false);
				}
			}
		},
		triggerClickDatetimePicker() {
			this.$refs.dateTimePickerRef.$children[0].pickerVisible = true;
		},
		resetAccessoryCurrentValue() {
			this.accessoryCurrentValue = 0;
			this.isResetAccessoryCurrentValue = true;
		}
	},
	components: {
		vSelect,
		CbsUploadRef,
		CbsTags
	},
	created() {
		this.fetchFormOptions();
	}
};
</script>

<style lang="scss">
.device-form--create-new .vs__dropdown-menu {
	position: relative;
}
</style>
